'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ArrowRight, CheckCircle2, GraduationCap, Building2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';
import CountrySelect, { CountryData } from '@/components/ui/CountrySelect';
import StateSelect, { StateData } from '@/components/ui/StateSelect';
import PhoneInputField, { PhoneData } from '@/components/ui/PhoneInputField';
import { useAuth } from '@/contexts/AuthContext';
import { sendRegistrationEmail } from '@/lib/email';

type AuthMode = 'login' | 'register';
type AccountType = 'student' | 'school';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface RegisterForm {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  schoolName?: string;
  grade?: string;
  terms: boolean;
}

export default function AuthPage() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<AuthMode>('login');
  const [accountType, setAccountType] = useState<AccountType>('student');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Location state
  const [countryData, setCountryData] = useState<CountryData>({ country_name: 'India', country_iso_code: 'IN', phone_country_code: '+91' });
  const [stateData, setStateData] = useState<StateData>({ state_name: '', state_iso_code: '' });
  const [phoneData, setPhoneData] = useState<PhoneData>({ phone_country_code: '+91', phone_number: '' });
  const [locationError, setLocationError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const loginForm = useForm<LoginForm>({ defaultValues: { remember: false } });
  const registerForm = useForm<RegisterForm>();

  const handleLogin = loginForm.handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back! Redirecting to your dashboard...');
      router.push('/student-dashboard');
      router.refresh();
    } catch (err: any) {
      const msg = err?.message || 'Invalid email or password';
      loginForm.setError('email', { message: msg });
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  });

  const handleRegister = registerForm.handleSubmit(async (data) => {
    let valid = true;
    if (!countryData.country_iso_code) {
      setLocationError('Please select your country');
      valid = false;
    } else {
      setLocationError('');
    }
    if (!phoneData.phone_number) {
      setPhoneError('Phone number is required');
      valid = false;
    } else {
      setPhoneError('');
    }
    if (!valid) return;

    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        account_type: accountType,
        grade: data.grade || '',
        school_name: accountType === 'school' ? data.fullName : (data.schoolName || ''),
        country_name: countryData.country_name,
        country_iso_code: countryData.country_iso_code,
        state_name: stateData.state_name,
        phone: `${phoneData.phone_country_code}${phoneData.phone_number}`,
      });
      toast.success('Account created! Signing you in...');
      // Send registration confirmation email (fire-and-forget)
      sendRegistrationEmail({
        to: data.email,
        fullName: data.fullName,
        accountType,
      });
      // Auto sign in after registration
      await signIn(data.email, data.password);
      router.push('/student-dashboard');
      router.refresh();
    } catch (err: any) {
      const msg = err?.message || 'Registration failed. Please try again.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  });

  const handleGoogleSignIn = async () => {
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
    } catch (err: any) {
      toast.error('Google sign-in failed. Please try email/password.');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-2/5 bg-hero-gradient flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-secondary blur-3xl" />
          <div className="absolute bottom-20 left-10 w-60 h-60 rounded-full bg-accent blur-3xl" />
        </div>
        <div className="relative">
          <Link href="/" className="flex items-center gap-2.5 mb-12">
            <AppLogo size={36} />
            <span className="font-extrabold text-lg text-white tracking-tight">
              FutureMinds<span className="text-secondary">.ai</span>
            </span>
          </Link>
          <h2 className="text-3xl font-extrabold text-white mb-4 leading-tight">
            Join 120,000+ students benchmarking their STEM excellence globally.
          </h2>
          <p className="text-slate-300 mb-8">
            International olympiads in Mathematics, Science, Computing, AI and more — with AI-powered report cards and global rankings.
          </p>
          <div className="space-y-3">
            {[
              'Take the exam anytime — no fixed window',
              'Detailed AI-generated performance insights',
              'Certificates generated on the spot & emailed instantly',
              'Global, country, state, city, and school rankings',
            ].map((item) => (
              <div key={`brand-${item}`} className="flex items-center gap-2.5">
                <CheckCircle2 size={16} className="text-secondary flex-shrink-0" />
                <span className="text-sm text-slate-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-bold text-sm">P</div>
              <div>
                <p className="text-white text-sm font-semibold">Priya K. — Global Rank #1,204</p>
                <p className="text-slate-400 text-xs">Science Olympiad 2025</p>
              </div>
            </div>
            <p className="text-slate-300 text-xs italic">&quot;The AI insights told me exactly what to work on. Went from 72nd percentile to 94th in one year.&quot;</p>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-16 bg-background overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          <Link href="/" className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-extrabold text-primary">FutureMinds<span className="text-secondary">.ai</span></span>
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-foreground mb-1">
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {mode === 'login' ? 'Sign in to access your dashboard and exams.' : 'Start your global olympiad journey today.'}
            </p>
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-xl border border-border bg-muted p-1 mb-6">
            <button
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setMode('login')}
            >
              Sign In
            </button>
            <button
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${mode === 'register' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setMode('register')}
            >
              Create Account
            </button>
          </div>

          {/* Account type selector */}
          <div className="flex gap-3 mb-6">
            <button
              className={`flex-1 flex items-center gap-2 px-4 py-3 border rounded-xl text-sm font-semibold transition-all ${accountType === 'student' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
              onClick={() => setAccountType('student')}
            >
              <GraduationCap size={16} />
              Student
            </button>
            <button
              className={`flex-1 flex items-center gap-2 px-4 py-3 border rounded-xl text-sm font-semibold transition-all ${accountType === 'school' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
              onClick={() => setAccountType('school')}
            >
              <Building2 size={16} />
              School
            </button>
          </div>

          {/* Google auth */}
          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors mb-5"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                <input
                  type="email"
                  {...loginForm.register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })}
                  className="w-full px-4 py-2.5 border border-input rounded-xl text-sm text-foreground bg-card focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                  placeholder="arjun@student.futureminds.ai"
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Password is required', minLength: { value: 6, message: 'Minimum 6 characters' } })}
                    className="w-full px-4 py-2.5 border border-input rounded-xl text-sm text-foreground bg-card focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent pr-10 transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" {...loginForm.register('remember')} className="w-4 h-4 rounded border-input accent-primary" />
                  <span className="text-sm text-muted-foreground">Remember me</span>
                </label>
                <a href="#" className="text-sm font-semibold text-primary hover:text-secondary transition-colors">Forgot password?</a>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 disabled:opacity-60 transition-all active:scale-95"
              >
                {isLoading ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <>Sign In <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  {accountType === 'student' ? 'Full Name' : 'School Name'}
                </label>
                <input
                  type="text"
                  {...registerForm.register('fullName', { required: 'This field is required' })}
                  className="w-full px-4 py-2.5 border border-input rounded-xl text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  placeholder={accountType === 'student' ? 'Arjun Mehta' : 'DPS Noida'}
                />
                {registerForm.formState.errors.fullName && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.fullName.message}</p>
                )}
              </div>

              {accountType === 'student' && (
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Grade / Class</label>
                  <select
                    {...registerForm.register('grade', { required: 'Select your grade' })}
                    className="w-full px-4 py-2.5 border border-input rounded-xl text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  >
                    <option value="">Select grade...</option>
                    {['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'].map((g) => (
                      <option key={`grade-${g}`} value={g}>{g}</option>
                    ))}
                  </select>
                  {registerForm.formState.errors.grade && (
                    <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.grade.message}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                <input
                  type="email"
                  {...registerForm.register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })}
                  className="w-full px-4 py-2.5 border border-input rounded-xl text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  placeholder="you@example.com"
                />
                {registerForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.email.message}</p>
                )}
              </div>

              {/* Country Select */}
              <CountrySelect
                value={countryData.country_iso_code}
                onChange={(data) => {
                  setCountryData(data);
                  setLocationError('');
                }}
                label="Country"
                required
                error={locationError}
              />

              {/* State Select */}
              <StateSelect
                countryCode={countryData.country_iso_code}
                value={stateData.state_iso_code}
                onChange={(data) => setStateData(data)}
                label="State / Province"
              />

              {/* Phone Input */}
              <PhoneInputField
                countryCode={countryData.country_iso_code}
                onChange={(data) => {
                  setPhoneData(data);
                  setPhoneError('');
                }}
                label="Phone Number"
                required
                error={phoneError}
              />

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...registerForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })}
                    className="w-full px-4 py-2.5 border border-input rounded-xl text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring pr-10 transition-all"
                    placeholder="Min. 8 characters"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {registerForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    {...registerForm.register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (val) => val === registerForm.watch('password') || 'Passwords do not match',
                    })}
                    className="w-full px-4 py-2.5 border border-input rounded-xl text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring pr-10 transition-all"
                    placeholder="Repeat password"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {registerForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...registerForm.register('terms', { required: 'You must accept the terms' })}
                    className="w-4 h-4 mt-0.5 rounded border-input accent-primary flex-shrink-0"
                  />
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    I agree to the{' '}
                    <a href="#" className="text-primary font-semibold hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="#" className="text-primary font-semibold hover:underline">Privacy Policy</a>
                  </span>
                </label>
                {registerForm.formState.errors.terms && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.terms.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 disabled:opacity-60 transition-all active:scale-95"
              >
                {isLoading ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <>Create Account <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}

          {/* Demo credentials */}
          <div className="mt-6 bg-muted/60 border border-border rounded-xl p-4">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Demo Account</p>
            <div className="bg-card rounded-lg px-3 py-2.5 border border-border">
              <p className="text-xs font-semibold text-foreground mb-0.5">Student — Arjun Mehta</p>
              <p className="text-xs text-muted-foreground font-mono">arjun.mehta@student.futureminds.ai</p>
              <p className="text-xs text-muted-foreground font-mono">Olympiad@2026</p>
              <button
                onClick={() => {
                  loginForm.setValue('email', 'arjun.mehta@student.futureminds.ai');
                  loginForm.setValue('password', 'Olympiad@2026');
                  setMode('login');
                  toast.info('Demo credentials filled');
                }}
                className="mt-2 px-3 py-1 text-xs font-semibold bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
              >
                Use Demo Account
              </button>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-5">
            {mode === 'login' ? "Don't have an account? " : 'Already registered? '}
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="font-semibold text-primary hover:text-secondary transition-colors"
            >
              {mode === 'login' ? 'Create one free' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}